import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import {
  AppointmentRefusedError,
  ETA_CHOICES,
  reachableAddresses,
  useRefreshJobAt,
  useSendOnMyWay,
  type AppointmentEmails,
  type VisitOnMyWay,
} from '@/routes/appointments/useAppointmentEmails';

/**
 * "On my way" (M2-21, Kyle's 4): a manual email, per visit, that the
 * technician is heading to the property — with an optional ETA of 15, 30, 45
 * or 60 minutes.
 *
 * <b>The API decides whether it can go, and says why not.</b> `onMyWay` on the
 * job's detail carries `canSend` and, when false, the very sentence a send
 * would be refused with: not the visit's day on the property's clock, inside
 * the ten-minute floor ("…can be sent again at 9:42 AM"), nobody who gets
 * appointment emails, no address, a finished job. The button is disabled
 * with that sentence beside it; the page works out none of it itself, so it
 * cannot disagree with the API about "today" on a clock it does not have.
 *
 * When the floor lifts the job is re-read (`useRefreshJobAt`), so the button
 * comes back without a reload.
 *
 * The confirm names the addresses it goes to when the API gives them — the
 * office's view (`appointmentEmails`, rule 5c). A technician is told it goes
 * to whoever gets appointment emails, without the addresses.
 */

/** Sentences for a failed send: the API's own, else a plain failure. */
function failureText(failure: Error, t: TFunction): string {
  return failure instanceof AppointmentRefusedError
    ? t('appointmentEmails.onMyWay.refused', { reason: failure.message })
    : t('appointmentEmails.onMyWay.failed');
}

function etaValue(choice: string): number | null {
  return choice === '' ? null : Number(choice);
}

/** The ETA choice and who it goes to — the body of the confirm, on either screen. */
function OnMyWayFields({
  eta,
  onEtaChange,
  emails,
  disabled,
}: {
  eta: string;
  onEtaChange: (value: string) => void;
  emails: AppointmentEmails | null | undefined;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const id = useId();
  const addresses = reachableAddresses(emails);
  const list = new Intl.ListFormat(navigator.language, { type: 'conjunction' }).format(addresses);

  return (
    <div className="space-y-3 text-sm">
      <fieldset disabled={disabled} className="space-y-1">
        <legend className="font-medium">{t('appointmentEmails.onMyWay.etaLegend')}</legend>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {['', ...ETA_CHOICES.map(String)].map((value) => (
            <label key={value || 'none'} className="flex items-center gap-1.5" htmlFor={`${id}-eta-${value || 'none'}`}>
              <input
                id={`${id}-eta-${value || 'none'}`}
                type="radio"
                name={`${id}-eta`}
                value={value}
                checked={eta === value}
                onChange={() => onEtaChange(value)}
              />
              <span>
                {value === ''
                  ? t('appointmentEmails.onMyWay.etaNone')
                  : t('appointmentEmails.onMyWay.etaMinutes', { count: Number(value) })}
              </span>
            </label>
          ))}
        </div>
      </fieldset>
      <p data-testid="on-my-way-recipients">
        {emails
          ? addresses.length > 0
            ? t('appointmentEmails.onMyWay.goesTo', { addresses: list })
            : t('appointmentEmails.onMyWay.goesToNobody')
          : t('appointmentEmails.onMyWay.goesToEveryone')}
      </p>
    </div>
  );
}

/**
 * The job page's "On my way" on one visit row: a button, disabled with the
 * API's sentence when it cannot go, that opens the confirm.
 */
export function OnMyWayButton({
  jobId,
  status,
  emails,
  visitLabel,
  onAnnounce,
}: {
  jobId: string;
  status: VisitOnMyWay;
  emails: AppointmentEmails | null | undefined;
  /** "Oct 24, 2026" or "Visit 2 of 3, Oct 24, 2026": names the visit in the label and the dialog. */
  visitLabel: string;
  onAnnounce: (message: string) => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const send = useSendOnMyWay(jobId);
  const [open, setOpen] = useState(false);
  const [eta, setEta] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  useRefreshJobAt(jobId, [status.nextAllowedAt]);

  function confirm() {
    setProblem(null);
    send.mutate(
      { visitId: status.visitId, etaMinutes: etaValue(eta) },
      {
        onSuccess: (sent) => {
          onAnnounce(t('appointmentEmails.onMyWay.sent', { count: Number(sent.recipients) }));
          setOpen(false);
        },
        onError: (failure) => setProblem(failureText(failure, t)),
      },
    );
  }

  return (
    <div className="flex flex-col items-start gap-1">
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={!status.canSend}
        aria-label={t('appointmentEmails.onMyWay.buttonLabel', { visit: visitLabel })}
        aria-describedby={!status.canSend && status.reason ? `${id}-reason` : undefined}
        onClick={() => {
          setEta('');
          setProblem(null);
          send.reset();
          setOpen(true);
        }}
      >
        {t('appointmentEmails.onMyWay.button')}
      </Button>
      {!status.canSend && status.reason ? (
        <p id={`${id}-reason`} className="max-w-xs text-xs text-[var(--color-muted-foreground)]" data-testid="on-my-way-reason">
          {status.reason}
        </p>
      ) : null}
      {open ? (
        <Dialog
          titleId={`${id}-title`}
          title={t('appointmentEmails.onMyWay.title')}
          description={visitLabel}
          onClose={() => setOpen(false)}
          testId="on-my-way-dialog"
          footer={
            <>
              <Button type="button" variant="outline" size="sm" onClick={() => setOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="button" size="sm" disabled={send.isPending} onClick={confirm}>
                {send.isPending ? t('appointmentEmails.onMyWay.sending') : t('appointmentEmails.onMyWay.send')}
              </Button>
            </>
          }
        >
          <OnMyWayFields eta={eta} onEtaChange={setEta} emails={emails} disabled={send.isPending} />
          {problem ? (
            <p role="alert" className="mt-3 text-sm text-[var(--color-destructive)]">
              {problem}
            </p>
          ) : null}
        </Dialog>
      ) : null}
    </div>
  );
}

/**
 * The board's "On my way", inside the card's own dialog (`MoveJobDialog`).
 * The card is a `<button>` and the drag handle, so a second control cannot
 * live on it (M1-13's reason for putting "View details" in that dialog); and
 * the dialog is already a modal, so this is the confirm itself rather than a
 * button opening a second modal over the first.
 */
export function OnMyWayPanel({
  jobId,
  status,
  emails,
}: {
  jobId: string;
  status: VisitOnMyWay | undefined;
  emails: AppointmentEmails | null | undefined;
}) {
  const { t } = useTranslation();
  const id = useId();
  const send = useSendOnMyWay(jobId);
  const [eta, setEta] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [sent, setSent] = useState('');
  useRefreshJobAt(jobId, [status?.nextAllowedAt]);

  function confirm() {
    if (!status) return;
    setProblem(null);
    setSent('');
    send.mutate(
      { visitId: status.visitId, etaMinutes: etaValue(eta) },
      {
        onSuccess: (result) => setSent(t('appointmentEmails.onMyWay.sent', { count: Number(result.recipients) })),
        onError: (failure) => setProblem(failureText(failure, t)),
      },
    );
  }

  return (
    <section aria-labelledby={`${id}-heading`} className="space-y-2 border-t pt-3" data-testid="on-my-way-panel">
      <h3 id={`${id}-heading`} className="text-sm font-semibold">
        {t('appointmentEmails.onMyWay.button')}
      </h3>
      {!status ? (
        <p className="text-sm text-[var(--color-muted-foreground)]">{t('appointmentEmails.onMyWay.loading')}</p>
      ) : status.canSend ? (
        <>
          <OnMyWayFields eta={eta} onEtaChange={setEta} emails={emails} disabled={send.isPending} />
          <Button type="button" size="sm" disabled={send.isPending} onClick={confirm}>
            {send.isPending ? t('appointmentEmails.onMyWay.sending') : t('appointmentEmails.onMyWay.send')}
          </Button>
        </>
      ) : (
        <>
          <Button type="button" size="sm" disabled aria-describedby={status.reason ? `${id}-reason` : undefined}>
            {t('appointmentEmails.onMyWay.send')}
          </Button>
          {status.reason ? (
            <p id={`${id}-reason`} className="text-xs text-[var(--color-muted-foreground)]" data-testid="on-my-way-reason">
              {status.reason}
            </p>
          ) : null}
        </>
      )}
      <p role="status" aria-live="polite" className="text-sm" data-testid="on-my-way-sent">
        {sent}
      </p>
      {problem ? (
        <p role="alert" className="text-sm text-[var(--color-destructive)]">
          {problem}
        </p>
      ) : null}
    </section>
  );
}
